import { useEffect, useState, type ReactNode } from "react";
import { Navigate, useNavigate } from "react-router";
import { useAuth } from "@/lib/auth-context";
import { apiGet, apiPost } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

interface QuestionOption {
  value: string;
  label: Record<string, string>;
}

interface Question {
  id: string;
  type: "select" | "text";
  required: boolean;
  label: Record<string, string>;
  placeholder?: Record<string, string>;
  options?: QuestionOption[];
}

interface Questionnaire {
  id: string;
  title: Record<string, string>;
  questions: Question[];
}

const locale = navigator.language.slice(0, 2);

function t(labels: Record<string, string>): string {
  return labels[locale] || labels["en"] || Object.values(labels)[0] || "";
}

function CenteredScreen({ children }: { children: ReactNode }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      {children}
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div className="flex flex-col items-center gap-4">
      <Skeleton className="h-10 w-10 rounded-full" />
      <Skeleton className="h-4 w-32" />
    </div>
  );
}

export function QuestionnairePage() {
  const { user, loading, pendingQuestionnaire, setPendingQuestionnaire } = useAuth();
  const navigate = useNavigate();
  const [questionnaire, setQuestionnaire] = useState<Questionnaire | null>(null);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user) return;
    apiGet<Questionnaire | null>("/api/questionnaires/next")
      .then((data) => {
        if (data) {
          setQuestionnaire(data);
        } else {
          setPendingQuestionnaire(false);
        }
      })
      .catch(() => setError("Failed to load questionnaire"));
  }, [user, setPendingQuestionnaire]);

  if (loading) {
    return <CenteredScreen><LoadingSkeleton /></CenteredScreen>;
  }

  if (!user) return <Navigate to="/login" replace />;
  if (pendingQuestionnaire === false) return <Navigate to="/" replace />;

  if (!questionnaire) {
    if (error) {
      return (
        <CenteredScreen>
          <p className="text-destructive text-sm">{error}</p>
        </CenteredScreen>
      );
    }
    return <CenteredScreen><LoadingSkeleton /></CenteredScreen>;
  }

  const canSubmit = questionnaire.questions
    .filter((q) => q.required)
    .every((q) => answers[q.id] !== undefined && answers[q.id] !== "");

  async function handleComplete() {
    if (!questionnaire) return;
    setSubmitting(true);
    setError(null);
    try {
      await apiPost(`/api/questionnaires/${questionnaire.id}/complete`, { answers });
      setPendingQuestionnaire(false);
      navigate("/");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to save");
    } finally {
      setSubmitting(false);
    }
  }

  function setAnswer(questionId: string, value: string) {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <div className="w-full max-w-lg">
        <h1 className="mb-6 text-center text-2xl font-semibold text-foreground">
          {t(questionnaire.title)}
        </h1>

        <div className="space-y-6">
          {questionnaire.questions.map((question) => (
            <div key={question.id} className="space-y-3">
              <label className="block text-sm font-medium text-foreground">
                {t(question.label)}
                {question.required && <span className="ml-1 text-destructive">*</span>}
              </label>

              {question.type === "select" && question.options && (
                <div className="grid gap-2">
                  {question.options.map((option) => {
                    const selected = answers[question.id] === option.value;
                    return (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => setAnswer(question.id, option.value)}
                        className={`rounded-lg border px-4 py-3 text-left text-sm transition-colors ${
                          selected
                            ? "border-primary bg-primary/10 text-foreground"
                            : "border-border bg-card text-muted-foreground hover:border-primary/50 hover:text-foreground"
                        }`}
                      >
                        {t(option.label)}
                      </button>
                    );
                  })}
                </div>
              )}

              {question.type === "text" && (
                <textarea
                  value={answers[question.id] ?? ""}
                  onChange={(e) => setAnswer(question.id, e.target.value)}
                  placeholder={question.placeholder ? t(question.placeholder) : ""}
                  rows={3}
                  className="w-full rounded-lg border border-border bg-card px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                />
              )}
            </div>
          ))}
        </div>

        {error && <p className="mt-4 text-center text-sm text-destructive">{error}</p>}

        <div className="mt-8 flex justify-end">
          <Button onClick={handleComplete} disabled={!canSubmit || submitting}>
            {submitting ? "Saving..." : "Continue"}
          </Button>
        </div>
      </div>
    </div>
  );
}
